import type { AcpSessionChangeData } from '@crowdex/agent-contracts';
import { toast } from '@crowdex/ui/components/toaster';
import type { ConversationScope, ConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import type { DesktopShellState, DesktopShellStore } from '@/lib/desktop-shell/store';
import { workspaceReplicaForThread } from '@/lib/desktop-shell/workspace-replicas';
import { errorMessage } from '@/lib/error-message';
import {
  type BranchTarget,
  commitNativeGitChanges,
  createNativeGitBranch,
  type DivergedBranchChoice,
  fetchNativeGitRemote,
  readNativeGitStatus,
  switchNativeGitBranch,
} from '@/lib/native-desktop';
import { withSessionEvent } from '@/lib/session-events';

export type BranchControls = {
  createBranch: () => Promise<void>;
  performBranchSwitch: (
    target: BranchTarget,
    stash?: boolean,
    diverged?: DivergedBranchChoice | null,
  ) => Promise<void>;
  /** Commits `paths` (the conversation's files) or, without, everything, then switches. */
  commitAndSwitchBranch: (
    target: BranchTarget,
    message: string,
    paths?: readonly string[],
  ) => Promise<void>;
  openWorktreeCheckout: (path: string) => Promise<void>;
  refreshRemoteBranches: () => Promise<void>;
  switchBranch: (target: BranchTarget) => void;
};

/** The conversations with a turn running in `folder`, by title. */
export function conversationsRunningIn(
  state: Pick<
    DesktopShellState,
    | 'chatId'
    | 'workspace'
    | 'loadingThreadIds'
    | 'conversationCache'
    | 'syncedThreads'
    | 'syncedWorkspaces'
    | 'generatedConversationTitle'
  >,
  folder: string,
): string[] {
  return [...state.loadingThreadIds].flatMap((id) => {
    const synced = state.syncedThreads.find((thread) => thread.id === id);
    const cached = state.conversationCache[id];
    const root =
      id === state.chatId
        ? state.workspace
        : (cached?.workspaceRoot ??
          (synced === undefined
            ? undefined
            : workspaceReplicaForThread(synced, state.syncedWorkspaces)?.rootPath));
    if (root !== folder) return [];
    const title =
      id === state.chatId ? state.generatedConversationTitle : (cached?.title ?? synced?.title);
    return [title ?? 'Another conversation'];
  });
}

/** Why the branch of `folder` cannot change now, or `null` when nothing runs in it. */
function branchChangeBlocked(state: DesktopShellState, folder: string): string | null {
  const [running, ...others] = conversationsRunningIn(state, folder);
  if (running === undefined) return null;
  const who = others.length === 0 ? `“${running}” is` : `${others.length + 1} conversations are`;
  return `${who} working in this folder: changing its branch now would change files under the agent. Wait for the turn to end, or stop it.`;
}

/**
 * Git branch / worktree actions for the desktop shell. Each action belongs to the
 * conversation shown when it starts (`ConversationScope`): git takes a while,
 * and what it settles (the status shown, the branch event in the transcript)
 * goes to that conversation even if the user opened another meanwhile. The
 * branch of a folder never changes while a turn runs in it, in any conversation.
 */
export function createBranchControls({
  store,
  conversations,
  preview,
  busy,
}: {
  store: DesktopShellStore;
  conversations: ConversationScopes;
  preview: boolean;
  busy: boolean;
}): BranchControls {
  function recordBranchChange(
    conversation: ConversationScope,
    changes: readonly AcpSessionChangeData[],
  ): void {
    conversation.setMessages((current) => withSessionEvent(current, changes));
  }

  /** Says why the folder's branch cannot change now; `true` when it can. */
  function branchMayChange(folder: string): boolean {
    const blocked = branchChangeBlocked(store.getState(), folder);
    if (blocked === null) return true;
    store.getState().setBranchError(blocked);
    toast.error('Couldn’t change branch', blocked);
    return false;
  }

  async function createBranch(): Promise<void> {
    const conversation = conversations.shown();
    const { workspace, newBranchName, gitStatus, setBranchError } = store.getState();
    if (workspace === null || preview || newBranchName.trim().length === 0) return;
    // A new branch starts where the folder is: its files stay as they are.
    setBranchError(null);
    try {
      const previousBranch = gitStatus?.branch ?? null;
      await createNativeGitBranch(workspace, newBranchName.trim());
      const nextStatus = await readNativeGitStatus(workspace);
      conversation.whileShown((state) => {
        state.setGitStatus(nextStatus);
        state.setCreateBranchOpen(false);
        state.setNewBranchName('');
      });
      recordBranchChange(conversation, [
        { field: 'branch', from: previousBranch, to: nextStatus.branch },
      ]);
      toast.success('Branch created', nextStatus.branch);
    } catch (error) {
      const message = errorMessage(error);
      conversation.whileShown((state) => state.setBranchError(message));
      toast.error('Couldn’t create branch', message);
    }
  }

  /**
   * Switch to `target`. A remote branch whose local namesake has commits of its own is not
   * switched until the user picks a side (`diverged`): the choice is kept in the store.
   */
  async function performBranchSwitch(
    target: BranchTarget,
    stash = false,
    diverged: DivergedBranchChoice | null = null,
  ): Promise<void> {
    const conversation = conversations.shown();
    const { workspace, gitStatus, setBranchError } = store.getState();
    if (workspace === null || preview) return;
    if (!branchMayChange(workspace)) return;
    setBranchError(null);
    conversation.update({ switchingBranch: true });
    try {
      const previousBranch = gitStatus?.branch ?? null;
      const result = await switchNativeGitBranch(workspace, target, stash, diverged);
      conversation.whileShown((state) => state.setPendingBranchSwitch(null));
      if (result.status === 'diverged') {
        conversation.whileShown((state) =>
          state.setDivergedBranch({
            branch: target.branch,
            ahead: result.ahead,
            behind: result.behind,
            stash,
          }),
        );
        return;
      }
      conversation.whileShown((state) => state.setDivergedBranch(null));
      const nextStatus = await readNativeGitStatus(workspace);
      conversation.whileShown((state) => state.setGitStatus(nextStatus));
      if (previousBranch !== nextStatus.branch)
        recordBranchChange(conversation, [
          { field: 'branch', from: previousBranch, to: nextStatus.branch },
        ]);
      toast.success(
        target.remote ? `Branch up to date with origin/${target.branch}` : 'Branch switched',
        nextStatus.branch,
      );
    } catch (error) {
      const message = errorMessage(error);
      conversation.whileShown((state) => state.setBranchError(message));
      toast.error('Couldn’t switch branch', message);
    } finally {
      conversation.update({ switchingBranch: false });
    }
  }

  async function commitAndSwitchBranch(
    target: BranchTarget,
    message: string,
    paths?: readonly string[],
  ): Promise<void> {
    const conversation = conversations.shown();
    const { workspace, setBranchError } = store.getState();
    if (workspace === null || preview || message.trim().length === 0) return;
    if (!branchMayChange(workspace)) return;
    setBranchError(null);
    conversation.update({ switchingBranch: true });
    try {
      await commitNativeGitChanges({
        workspaceRoot: workspace,
        message: message.trim(),
        push: false,
        ...(paths === undefined ? {} : { paths }),
      });
    } catch (error) {
      const failure = errorMessage(error);
      conversation.whileShown((state) => state.setBranchError(failure));
      toast.error('Couldn’t commit and switch', failure);
      conversation.update({ switchingBranch: false });
      return;
    }
    // Opened elsewhere meanwhile: the commit is made, the switch waits for the user.
    if (!conversation.shown()) {
      conversation.update({ switchingBranch: false });
      return;
    }
    await performBranchSwitch(target);
  }

  /** Fetches the remote so the pickers list its current branches. */
  async function refreshRemoteBranches(): Promise<void> {
    const conversation = conversations.shown();
    const { workspace } = store.getState();
    if (workspace === null || preview) return;
    try {
      await fetchNativeGitRemote(workspace);
      const status = await readNativeGitStatus(workspace);
      conversation.whileShown((state) => {
        if (state.workspace === workspace) state.setGitStatus(status);
      });
    } catch (error) {
      toast.error('Couldn’t refresh remote branches', errorMessage(error));
    }
  }

  async function openWorktreeCheckout(path: string): Promise<void> {
    const conversation = conversations.shown();
    store.getState().setBranchError(null);
    conversation.update({ switchingBranch: true });
    try {
      conversation.place({ workspace: path, executionMode: 'local' });
      const status = await readNativeGitStatus(path).catch(() => null);
      conversation.whileShown((state) => {
        state.setGitStatus(status);
        state.setBranchInWorktree(null);
      });
    } finally {
      conversation.update({ switchingBranch: false });
    }
  }

  function switchBranch(target: BranchTarget): void {
    const { workspace, gitStatus, setBranchError, setBranchInWorktree, setPendingBranchSwitch } =
      store.getState();
    const { branch } = target;
    // The menu only renders before a conversation starts, but guard against a
    // streaming agent mutating the checkout underneath it regardless.
    if (workspace === null || preview || busy) return;
    if (!target.remote && branch === (gitStatus?.branch ?? '')) return;
    // Git forbids checking out a branch that another worktree already holds.
    // Offer to open that worktree instead of failing on the raw git error.
    const occupied = gitStatus?.worktrees.find((tree) => !tree.isCurrent && tree.branch === branch);
    if (occupied !== undefined) {
      setBranchError(null);
      setBranchInWorktree({ branch, path: occupied.path });
      return;
    }
    // Another conversation's agent works in this folder: its files must not change under it.
    if (!branchMayChange(workspace)) return;
    // Dirty working tree: confirm first so the carry-over/abort outcome of
    // `git switch` is explicit instead of surfacing as a raw error.
    if (gitStatus?.hasChanges === true) {
      setBranchError(null);
      setPendingBranchSwitch(target);
      return;
    }
    void performBranchSwitch(target);
  }

  return {
    createBranch,
    performBranchSwitch,
    commitAndSwitchBranch,
    openWorktreeCheckout,
    refreshRemoteBranches,
    switchBranch,
  };
}
